// src/pages/shop/ShopWholesaleSaleDm.jsx
//
// Режим отгрузки оптовику по DataMatrix.
// Сканируем каждую единицу → полный код падает в список с порядковым №.
// Дубли ловятся на лету: в этой накладной и по всей истории отгрузок.
// Название товара подтягивается по GTIN (как в чекере маркировки).

import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { wholesaleSales as wsApi, products as productsApi, dmReservations } from '../../api';
import { useToast } from '../../hooks/useToast';
import Icon from '../../components/Icon';
import ScanButton from '../../components/ScanButton';
import { displayCode, extractGtin } from '../../utils/marking';

// Нормализация как на бэке: срезаем symbology prefix сканера (]d2 …)
function normalizeCode(raw) {
  if (!raw) return '';
  return String(raw).replace(/^\][A-Za-z][0-9]/, '').trim();
}

export default function ShopWholesaleSaleDm({ clients = [], presetClientId = '' }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { show, ToastView } = useToast();

  const [clientId, setClientId] = useState(presetClientId || '');
  const [units, setUnits] = useState([]); // [{ seq, code, productId, name, salePrice, unit, costPrice, onStock }]
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  // Режим сверки этикеток: N сканов одного кода (товар + упаковка), проверка идентичности
  const [verifyLabels, setVerifyLabels] = useState(() => {
    try { return localStorage.getItem('alsatuu_dm_verify_labels') === '1'; } catch (e) { return false; }
  });
  const [labelCount, setLabelCount] = useState(() => {
    try { const n = parseInt(localStorage.getItem('alsatuu_dm_label_count'), 10); return (n >= 2 && n <= 9) ? n : 2; } catch (e) { return 2; }
  });
  const [pending, setPending] = useState([]); // накопленные сканы текущей единицы

  useEffect(() => { try { localStorage.setItem('alsatuu_dm_verify_labels', verifyLabels ? '1' : '0'); } catch (e) { /* ignore */ } }, [verifyLabels]);
  useEffect(() => { try { localStorage.setItem('alsatuu_dm_label_count', String(labelCount)); } catch (e) { /* ignore */ } }, [labelCount]);

  // Heartbeat брони: пока экран открыт, раз в 5 мин продлеваем брони кодов накладной.
  // При выходе с экрана — снимаем свои брони (иначе протухнут сами за ~12 мин).
  const unitsRef = useRef([]);
  useEffect(() => { unitsRef.current = units; }, [units]);
  useEffect(() => {
    const id = setInterval(() => {
      const codes = unitsRef.current.map(u => u.code);
      if (codes.length) dmReservations.heartbeat(codes).catch(() => {});
    }, 5 * 60 * 1000);
    return () => {
      clearInterval(id);
      const codes = unitsRef.current.map(u => u.code);
      if (codes.length) dmReservations.release(codes).catch(() => {});
    };
  }, []);

  const client = clients.find(c => c.id === clientId) || null;

  // Записать проверенный код в накладную (дубли: в накладной + по истории)
  const addCode = async (code) => {
    const dup = units.find(u => u.code === code);
    if (dup) { show(t('wholesaleDm.dupInList', { seq: dup.seq }), 'error'); return false; }
    setBusy(true);
    try {
      const chk = await wsApi.checkCode(code).catch(() => ({ used: false }));
      if (chk && chk.used) {
        const dt = chk.date ? new Date(chk.date).toLocaleDateString('ru-RU') : '';
        show(t('wholesaleDm.dupHistory', { date: dt, number: chk.saleNumber || '?' }), 'error');
        return false;
      }
      // Бронь: не занят ли код другим сборщиком (параллельная сборка)
      const rsv = await dmReservations.reserve(code).catch(() => ({ ok: true }));
      if (rsv && rsv.busy) {
        show(t('wholesaleDm.codeBusy', { by: rsv.by || t('wholesaleDm.someone') }), 'error');
        return false;
      }
      let product = null;
      try { const r = await productsApi.byBarcode(code); product = r?.product || null; } catch { /* нет на складе */ }
      setUnits(prev => [...prev, {
        seq: prev.length + 1,
        code,
        productId: product?.id || null,
        name: product?.name || null,
        salePrice: Number(product?.salePrice) || 0,
        unit: product?.unit || 'шт',
        costPrice: Number(product?.costPrice) || 0,
        onStock: !!product,
      }]);
      return true;
    } finally {
      setBusy(false);
    }
  };

  const handleScan = async (raw) => {
    const code = normalizeCode(raw);
    if (!code) return;

    // Обычный режим — один скан = одна единица
    if (!verifyLabels) { await addCode(code); return; }

    // Режим сверки этикеток
    if (pending.length === 0) {
      // ранняя проверка дубля в накладной, чтобы не сканировать вторую этикетку зря
      const dup = units.find(u => u.code === code);
      if (dup) { show(t('wholesaleDm.dupInList', { seq: dup.seq }), 'error'); return; }
      setPending([code]);
      show(t('wholesaleDm.labelProgress', { n: 1, total: labelCount }));
      return;
    }
    if (code !== pending[0]) {
      setPending([]);
      show(t('wholesaleDm.labelMismatch'), 'error');
      return;
    }
    const next = [...pending, code];
    if (next.length >= labelCount) {
      setPending([]);
      const ok = await addCode(code);
      if (ok) show(t('wholesaleDm.labelMatched'));
    } else {
      setPending(next);
      show(t('wholesaleDm.labelProgress', { n: next.length, total: labelCount }));
    }
  };

  const removeUnit = (seq) => {
    setUnits(prev => {
      const removed = prev.find(u => u.seq === seq);
      if (removed) dmReservations.release(removed.code).catch(() => {});
      return prev.filter(u => u.seq !== seq).map((u, i) => ({ ...u, seq: i + 1 }));
    });
  };

  const save = async () => {
    if (!clientId) { show(t('wholesale.errSelectClient'), 'error'); return; }
    if (units.length === 0) { show(t('wholesaleDm.errEmpty'), 'error'); return; }

    // Группируем единицы по товару → позиции накладной (qty = количество кодов)
    const byProduct = new Map();
    for (const u of units) {
      const key = u.productId || `__none__${u.name || u.code}`;
      const g = byProduct.get(key) || {
        productId: u.productId, name: u.name || t('wholesaleDm.unknownProduct'),
        unit: u.unit, salePrice: u.salePrice, costPrice: u.costPrice, qty: 0,
      };
      g.qty += 1;
      byProduct.set(key, g);
    }
    const items = [...byProduct.values()].map(g => ({
      productId: g.productId || null,
      name: g.name, qty: g.qty, unit: g.unit,
      salePrice: g.salePrice, costPrice: g.costPrice,
    }));
    const codes = units.map(u => ({
      code: u.code, seq: u.seq, productId: u.productId,
      name: u.name, gtin: extractGtin(u.code),
    }));

    if (!window.confirm(t('wholesaleDm.confirmShip', { client: client?.name || '', count: units.length }))) return;

    setSaving(true);
    try {
      const sale = await wsApi.create({ counterpartyId: clientId, items, codes, note: note.trim() || undefined });
      show(t('wholesale.shipped'));
      navigate(`/counterparty/${sale.counterparty.id}`);
    } catch (e) {
      show(e.message || t('common.error'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page" style={{ paddingBottom: 32 }}>
      {ToastView}
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate(-1)}><Icon name="chevronLeft" size={18} /></button>
        <h1 className="page-title">{t('wholesaleDm.title')}</h1>
      </div>

      <div style={{ padding: '0 20px' }}>
        {/* Клиент */}
        <div className="input-group">
          <label>{t('wholesale.clientLabel')}</label>
          {clients.length === 0 ? (
            <div style={{ padding: 12, background: 'var(--bg-subtle)', borderRadius: 'var(--r-sm)', fontSize: 13, color: 'var(--fg-muted)' }}>
              {t('wholesale.noClients')}
              <button onClick={() => navigate('/counterparties')} className="btn btn-secondary btn-sm" style={{ marginTop: 8, width: '100%' }}>
                {t('wholesale.goToCounterparties')}
              </button>
            </div>
          ) : (
            <select className="select" value={clientId} onChange={(e) => setClientId(e.target.value)}>
              <option value="">— {t('common.notSet')} —</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
        </div>

        {/* Сверка этикеток (товар + упаковка) */}
        <div className="input-group">
          <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', fontWeight: 700 }}>
            <span>{t('wholesaleDm.verifyLabels')}</span>
            <input type="checkbox" checked={verifyLabels} onChange={(e) => { setVerifyLabels(e.target.checked); setPending([]); }} />
          </label>
          <p style={{ fontSize: 12, color: 'var(--fg-faint)', marginTop: 4, lineHeight: 1.3 }}>{t('wholesaleDm.verifyHint')}</p>
          {verifyLabels && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
              <span style={{ fontSize: 13, color: 'var(--fg-muted)' }}>{t('wholesaleDm.labelCount')}</span>
              <button className="btn btn-ghost" onClick={() => { setLabelCount(c => Math.max(2, c - 1)); setPending([]); }} style={{ width: 'auto', flexShrink: 0, padding: '2px 12px' }}><Icon name="minus" size={14} /></button>
              <span style={{ minWidth: 22, textAlign: 'center', fontWeight: 800, fontSize: 15 }}>{labelCount}</span>
              <button className="btn btn-ghost" onClick={() => { setLabelCount(c => Math.min(9, c + 1)); setPending([]); }} style={{ width: 'auto', flexShrink: 0, padding: '2px 12px' }}><Icon name="plus" size={14} /></button>
            </div>
          )}
        </div>

        {/* Скан */}
        <div className="input-group">
          <label>{t('wholesaleDm.scanLabel')}</label>
          <ScanButton raw variant="button" label={t('wholesaleDm.scanBtn')} size="lg" onScan={handleScan} style={{ width: '100%' }} />
          {busy && <div style={{ fontSize: 12, color: 'var(--fg-muted)', marginTop: 6 }}>{t('wholesaleDm.checking')}</div>}
          {verifyLabels && pending.length > 0 && (
            <div style={{ marginTop: 8, padding: 10, background: 'var(--brand-primary-soft)', borderRadius: 'var(--r-sm)', fontSize: 13, fontWeight: 700, color: 'var(--brand-primary)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>{t('wholesaleDm.labelWaiting', { n: pending.length + 1, total: labelCount })}</span>
              <button className="btn btn-ghost" onClick={() => setPending([])} style={{ width: 'auto', flexShrink: 0, padding: '2px 8px', fontSize: 12 }}>{t('wholesaleDm.labelReset')}</button>
            </div>
          )}
        </div>

        {/* Счётчик */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '4px 0 10px' }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--fg-muted)' }}>{t('wholesaleDm.collected')}</span>
          <span style={{ fontSize: 15, fontWeight: 800 }}>{units.length}</span>
        </div>

        {/* Список единиц (последние сверху) */}
        {units.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--fg-faint)', fontSize: 13 }}>{t('wholesaleDm.emptyHint')}</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {[...units].reverse().map(u => (
              <div key={u.seq} className="card" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px' }}>
                <span style={{ fontWeight: 800, fontSize: 14, color: 'var(--fg-muted)', minWidth: 30 }}>№{u.seq}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: u.onStock ? 'var(--fg)' : 'var(--danger)' }}>
                    {u.onStock ? u.name : t('marking.notOnStock')}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--fg-faint)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {displayCode(u.code)}
                  </div>
                </div>
                <button className="btn btn-ghost" onClick={() => removeUnit(u.seq)} style={{ padding: '0 8px' }}>
                  <Icon name="x" size={16} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Комментарий */}
        <div className="input-group" style={{ marginTop: 14 }}>
          <label>{t('wholesale.note')}</label>
          <textarea className="textarea" value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('wholesale.notePh')} rows={2} />
        </div>

        <button className="btn btn-primary" onClick={save} disabled={saving || units.length === 0} style={{ width: '100%', marginTop: 8 }}>
          {saving ? '…' : t('wholesaleDm.shipBtn', { count: units.length })}
        </button>
      </div>
    </div>
  );
}
